import React, { useState, useRef, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { Camera, X } from 'lucide-react';
import { uploadSiteImage } from '../../services/image-upload-service';

// Seed the cropper at zoom > 1 so the image has slack inside the crop circle
// and drag-to-reposition works immediately. Slider min stays at 1 so users
// can still zoom out for an already-square photo. (B2 fix.)
const DEFAULT_ZOOM = 1.2;

async function getCroppedImgBlob(imageSrc, pixelCrop, outputSize = 400) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext('2d');

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputSize,
    outputSize
  );

  // Return blob instead of base64
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.85);
  });
}

function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.crossOrigin = 'anonymous';
    image.src = url;
  });
}

const PhotoUpload = ({ currentPhoto, onPhotoChange, showIntro = true }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setImageSrc(reader.result);
      setShowCropModal(true);
      setCrop({ x: 0, y: 0 });
      setZoom(DEFAULT_ZOOM);
      setUploadError(null);
    };
    reader.readAsDataURL(file);
    // Reset input so the same file can be re-selected
    e.target.value = '';
  };

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    setUploading(true);
    setUploadError(null);

    try {
      // Get cropped blob
      const croppedBlob = await getCroppedImgBlob(imageSrc, croppedAreaPixels, 400);

      // Create File object from blob
      const file = new File([croppedBlob], 'profile-photo.jpg', { type: 'image/jpeg' });

      // Try Firebase Storage upload
      const userId = localStorage.getItem('userId') || 'default';
      const result = await uploadSiteImage(file, 'profile-photos', userId);

      if (result.success) {
        // Firebase upload succeeded
        onPhotoChange(result.url, result.fullPath || null);
        setShowCropModal(false);
        setImageSrc(null);
      } else if (result.error && result.error.includes('permission')) {
        // Firebase Storage not available yet (Phase D) - fall back to Base64
        console.log('Firebase Storage not available yet, using Base64 fallback');

        // Convert blob to Base64
        const reader = new FileReader();
        reader.onloadend = () => {
          onPhotoChange(reader.result, null); // Base64 — no storage path
          setShowCropModal(false);
          setImageSrc(null);
        };
        reader.readAsDataURL(croppedBlob);
      } else {
        // Other error - show to user
        setUploadError(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Photo upload error:', error);

      // If it's a storage permission error, fall back silently
      if (error.code === 'storage/unauthorized' || error.message?.includes('permission')) {
        console.log('Firebase Storage not available yet, using Base64 fallback');

        // Fall back to Base64
        const croppedBlob = await getCroppedImgBlob(imageSrc, croppedAreaPixels, 400);
        const reader = new FileReader();
        reader.onloadend = () => {
          onPhotoChange(reader.result, null); // Base64 fallback — no storage path
          setShowCropModal(false);
          setImageSrc(null);
        };
        reader.readAsDataURL(croppedBlob);
      } else {
        // Other error - show to user
        setUploadError('Failed to upload photo. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setShowCropModal(false);
    setImageSrc(null);
  };

  // Close on Escape
  useEffect(() => {
    if (!showCropModal) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') handleCancel();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [showCropModal]);

  // Focus trap
  useEffect(() => {
    if (!showCropModal || !modalRef.current) return;
    const focusable = modalRef.current.querySelectorAll('button, input[type="range"]');
    if (focusable.length) focusable[0].focus();
  }, [showCropModal]);

  return (
    <div>
      {showIntro && (
        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
          A clear photo of your face helps clients feel connected to you before they even book. Choose a photo where you're easy to recognize — it doesn't need to be professional, just authentically you.
        </p>
      )}

      <div className="flex flex-col items-center">
        {/* Preview / Upload trigger */}
        <span
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
          className="relative group cursor-pointer"
        >
          <div className="w-24 h-24 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden group-hover:border-df-purple transition-colors">
            {currentPhoto ? (
              <img
                src={currentPhoto}
                alt="Your profile photo"
                className="w-full h-full object-cover"
              />
            ) : (
              <Camera className="w-8 h-8 text-gray-400 group-hover:text-df-purple transition-colors" />
            )}
          </div>
        </span>

        <span
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
          className="mt-2 text-sm text-df-purple hover:text-df-purple-hover font-medium cursor-pointer transition-colors"
        >
          {currentPhoto ? 'Change Photo' : 'Add Photo'}
        </span>

        {currentPhoto && (
          <p className="text-sm text-gray-600 mt-1">This is how clients will see you</p>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          aria-label="Upload profile photo"
        />
      </div>

      {/* Crop Modal */}
      {showCropModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/60"
            onClick={handleCancel}
          />

          {/* Modal */}
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-label="Crop your photo"
            className="relative bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-2">
              <div>
                <h3 className="text-lg font-semibold text-df-charcoal">Position Your Photo</h3>
                <p className="text-xs text-gray-500">Drag to reposition, scroll or pinch to zoom</p>
              </div>
              <button
                onClick={handleCancel}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Crop area */}
            <div className="relative w-full aspect-square bg-gray-900">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            {/* Zoom slider */}
            <div className="px-6 py-4">
              <label className="flex items-center gap-3">
                <span className="text-xs text-gray-500">Zoom</span>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-grow h-1.5 rounded-full appearance-none bg-gray-200 accent-df-purple cursor-pointer"
                />
              </label>
            </div>

            {/* Error message */}
            {uploadError && (
              <div className="px-6 pb-3">
                <p className="text-xs text-red-600">{uploadError}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-4 px-6 pb-5">
              <button
                onClick={handleCancel}
                disabled={uploading}
                className="text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={uploading}
                className="bg-df-gold hover:bg-df-gold-dark text-white font-semibold text-sm px-6 py-2.5 rounded-xl transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-wait"
              >
                {uploading ? 'Uploading...' : 'Save Photo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;
